import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/Dialog';
import { getBlackoutDates, saveBlackoutDate, updateBlackoutDate, deleteBlackoutDate, getCompanyLeaveTypes } from '../../../../../api/Attendance/leaves';
import { getDepartments } from '../../../../../api/General/Company';
import type { LeavePolicyView, BlackoutPeriodView, BlackoutPeriodInput, LeaveTypeView } from '../../../../../models/attendance';
import type { Department } from '../../../../../models/general';
import { BlackoutSeverity } from '../../../../../models/attendance';

interface BlackoutDatesTabProps {
  currentPolicy?: LeavePolicyView;
}

// Helpers: comma-separated string <-> array
const csvToArray = (csv?: string): string[] =>
  csv ? csv.split(',').map(s => s.trim()).filter(Boolean) : [];

const arrayToCsv = (arr: string[]): string => arr.join(',');

// Parse stored value: try JSON array first, fall back to CSV for legacy data
const parseStoredIds = (value?: string): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch { /* not JSON, fall back to CSV */ }
  return csvToArray(value);
};

// Form state mirrors BlackoutPeriodInput but keeps arrays for multi-selects
interface BlackoutFormState {
  name: string;
  startDate: string;
  endDate: string;
  severity: BlackoutSeverity;
  reason: string;
  departments: string[];
  leaveTypes: string[];
  notificationDaysBefore: number;
}

const emptyForm: BlackoutFormState = {
  name: '',
  startDate: '',
  endDate: '',
  severity: BlackoutSeverity.Advisory,
  reason: '',
  departments: [],
  leaveTypes: [],
  notificationDaysBefore: 30,
};

const BlackoutDatesTab: React.FC<BlackoutDatesTabProps> = ({ currentPolicy }) => {
  const { t } = useTranslation('leaves');
  const companyId = useSelector((state: any) => state.user?.company?.id);

  const [blackoutPeriods, setBlackoutPeriods] = useState<BlackoutPeriodView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<BlackoutPeriodView | null>(null);
  const [formState, setFormState] = useState<BlackoutFormState>({ ...emptyForm });

  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const [apiDepartments, setApiDepartments] = useState<Department[]>([]);
  const [apiLeaveTypes, setApiLeaveTypes] = useState<LeaveTypeView[]>([]);

  // ── Load departments and leave types from API ──
  useEffect(() => {
    if (companyId) {
      getDepartments(companyId).then(result => {
        if (!result?.errors?.length) setApiDepartments(result.payload ?? []);
      }).catch(() => {});
    }
    getCompanyLeaveTypes().then(result => {
      if (!result?.errors?.length) setApiLeaveTypes(result.payload ?? []);
    }).catch(() => {});
  }, [companyId]);

  // ── Load blackout dates when policy changes ──
  useEffect(() => {
    if (currentPolicy?.id) loadBlackoutDates();
  }, [currentPolicy?.id]);

  const loadBlackoutDates = async () => {
    if (!currentPolicy?.id) return;
    setIsLoading(true);
    try {
      const result = await getBlackoutDates(currentPolicy.id);
      if (!result?.errors?.length) {
        setBlackoutPeriods(result.payload ?? []);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Options (fetched from API) ──
  const departmentOptions = [
    { value: 'All Departments', label: t('All Departments') },
    ...apiDepartments.map(d => ({ value: String(d.id), label: d.name }))
  ];

  const leaveTypeOptions = apiLeaveTypes.map(lt => ({ value: String(lt.id), label: lt.name }));

  const severityOptions = [
    { value: BlackoutSeverity.Strict, label: t('Strict Block') },
    { value: BlackoutSeverity.Advisory, label: t('Advisory Warning') },
    { value: BlackoutSeverity.Conditional, label: t('Conditional Approval') }
  ];

  const monthNames = [t('January'), t('February'), t('March'), t('April'), t('May'), t('June'), t('July'), t('August'), t('September'), t('October'), t('November'), t('December')];

  // ── Calendar helpers ──
  const generateCalendar = () => {
    const year = currentMonth?.getFullYear();
    const month = currentMonth?.getMonth();
    const daysInMonth = new Date(year, month + 1, 0)?.getDate();
    const calendar = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = new Date(year, month, day)?.toISOString()?.split('T')?.[0];
      const blackouts = blackoutPeriods?.filter(p => dateStr >= p?.startDate && dateStr <= p?.endDate);
      calendar?.push({ date: day, dateStr, isBlackout: blackouts?.length > 0, blackouts });
    }
    return calendar;
  };

  // Data-differentiation colors — kept as-is per spec
  const getSeverityColor = (severity: BlackoutSeverity | string): string => {
    switch (severity) {
      case BlackoutSeverity.Strict: return 'bg-red-100 text-red-800 border-red-200';
      case BlackoutSeverity.Advisory: return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case BlackoutSeverity.Conditional: return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getCalendarDayBg = (severity: BlackoutSeverity | string): string => {
    switch (severity) {
      case BlackoutSeverity.Strict: return 'bg-red-100';
      case BlackoutSeverity.Advisory: return 'bg-yellow-100';
      case BlackoutSeverity.Conditional: return 'bg-blue-100';
      default: return 'bg-card';
    }
  };

  const getCalendarBadgeBg = (severity: BlackoutSeverity | string): string => {
    switch (severity) {
      case BlackoutSeverity.Strict: return 'bg-red-200 text-red-800';
      case BlackoutSeverity.Advisory: return 'bg-yellow-200 text-yellow-800';
      case BlackoutSeverity.Conditional: return 'bg-blue-200 text-blue-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  // ── Form → API input ──
  const formToInput = (form: BlackoutFormState, isActive = true): BlackoutPeriodInput => ({
    name: form.name,
    startDate: form.startDate,
    endDate: form.endDate,
    severity: form.severity,
    reason: form.reason || undefined,
    departments: form.departments.length ? JSON.stringify(form.departments.filter(d => d !== 'All Departments').map(Number)) : undefined,
    leaveTypes: form.leaveTypes.length ? JSON.stringify(form.leaveTypes.map(Number)) : undefined,
    notificationDaysBefore: form.notificationDaysBefore,
    isActive,
  });

  // ── API view → form state (for editing) ──
  const viewToForm = (view: BlackoutPeriodView): BlackoutFormState => ({
    name: view.name,
    startDate: view.startDate,
    endDate: view.endDate,
    severity: view.severity,
    reason: view.reason ?? '',
    departments: parseStoredIds(view.departments),
    leaveTypes: parseStoredIds(view.leaveTypes),
    notificationDaysBefore: view.notificationDaysBefore,
  });

  // ── CRUD handlers ──
  const handleSavePeriod = async (): Promise<void> => {
    if (!currentPolicy?.id || !formState.name || !formState.startDate || !formState.endDate) return;
    setIsSaving(true);
    try {
      const input = formToInput(formState);
      const result = await saveBlackoutDate(currentPolicy.id, input);
      if (!result?.errors?.length) {
        await loadBlackoutDates();
        resetForm();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditPeriod = (period: BlackoutPeriodView): void => {
    setEditingPeriod(period);
    setFormState(viewToForm(period));
    setShowModal(true);
  };

  const handleUpdatePeriod = async (): Promise<void> => {
    if (!currentPolicy?.id || !editingPeriod) return;
    setIsSaving(true);
    try {
      const input = formToInput(formState, editingPeriod.isActive);
      const result = await updateBlackoutDate(editingPeriod.id, input);
      if (!result?.errors?.length) {
        await loadBlackoutDates();
        resetForm();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePeriod = async (id: number): Promise<void> => {
    if (!currentPolicy?.id) return;
    if (!window.confirm(t('Are you sure you want to delete this item?'))) return;
    const result = await deleteBlackoutDate(currentPolicy.id, id);
    if (!result?.errors?.length) {
      await loadBlackoutDates();
    }
  };

  const resetForm = (): void => {
    setShowModal(false);
    setEditingPeriod(null);
    setFormState({ ...emptyForm });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('Blackout Dates')}</h3>
          <p className="text-sm text-muted-foreground">{t('Define periods when leave requests are restricted or require special approval')}</p>
        </div>
        <span title={!currentPolicy?.id ? t('selectPolicyFirst') : undefined}>
          <Button variant="default" iconName="Plus" onClick={() => setShowModal(true)} disabled={!currentPolicy?.id} data-testid="add-blackout-btn">
            {t('Add Blackout Period')}
          </Button>
        </span>
      </div>

      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          <Icon name="Info" size={20} className="text-destructive mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('Blackout Dates Configuration')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('Define periods when leave requests are restricted or require special approval. Useful for peak business periods or critical project phases.')}
            </p>
          </div>
        </div>
      </div>

      {currentPolicy && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{t('Current Policy')}: {currentPolicy?.name}</span>
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={24} className="animate-spin text-primary me-2" />
          <span className="text-sm text-muted-foreground">{t('Loading blackout dates...')}</span>
        </div>
      )}

      {/* No policy selected */}
      {!currentPolicy?.id && !isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="AlertCircle" size={40} className="mx-auto mb-3 text-muted-foreground/60" />
          <p className="text-sm">{t('Please select a policy to manage blackout dates')}</p>
        </div>
      )}

      {/* Calendar View */}
      {!isLoading && currentPolicy?.id && (
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-md font-semibold text-foreground">{t('Blackout Calendar')}</h4>
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <button onClick={() => setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() - 1))} className="p-1 hover:bg-muted rounded">
                <Icon name="ChevronLeft" size={20} />
              </button>
              <span className="text-md font-medium">{monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}</span>
              <button onClick={() => setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() + 1))} className="p-1 hover:bg-muted rounded">
                <Icon name="ChevronRight" size={20} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {[t('Sun'), t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat')]?.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">{day}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {generateCalendar()?.map((day, index) => (
              <div key={index} className={`p-2 min-h-[60px] border border-border ${
                day?.isBlackout
                  ? getCalendarDayBg(day?.blackouts?.[0]?.severity)
                  : 'bg-card'
              }`}>
                <div className="text-sm font-medium text-foreground">{day?.date}</div>
                {day?.blackouts?.map((bo, bi) => (
                  <div key={bi} className={`text-xs mt-1 px-1 py-0.5 rounded truncate ${getCalendarBadgeBg(bo?.severity)}`} title={bo?.name}>{bo?.name}</div>
                ))}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center space-x-6 rtl:space-x-reverse mt-4 pt-4 border-t border-border">
            <div className="flex items-center space-x-2 rtl:space-x-reverse"><div className="w-4 h-4 bg-red-100 border border-red-200 rounded"></div><span className="text-sm text-muted-foreground">{t('Strict Block')}</span></div>
            <div className="flex items-center space-x-2 rtl:space-x-reverse"><div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded"></div><span className="text-sm text-muted-foreground">{t('Advisory Warning')}</span></div>
            <div className="flex items-center space-x-2 rtl:space-x-reverse"><div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded"></div><span className="text-sm text-muted-foreground">{t('Conditional')}</span></div>
          </div>
        </div>
      )}

      {/* Blackout Periods List */}
      {!isLoading && currentPolicy?.id && (
        <div className="space-y-4">
          {blackoutPeriods?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground border border-dashed border-border rounded-lg">
              <Icon name="CalendarOff" size={40} className="mx-auto mb-3 text-muted-foreground/60" />
              <p className="text-sm">{t('No blackout periods configured for this policy')}</p>
            </div>
          )}
          {blackoutPeriods?.map((period) => (
            <div key={period?.id} className="bg-card border border-border rounded-lg p-6" data-testid="blackout-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div>
                    <h4 className="text-md font-semibold text-foreground">{period?.name}</h4>
                    <p className="text-sm text-muted-foreground">{new Date(period?.startDate)?.toLocaleDateString()} - {new Date(period?.endDate)?.toLocaleDateString()}</p>
                  </div>
                  <span className={`px-3 py-1 text-sm rounded-full border ${getSeverityColor(period?.severity)}`}>
                    {severityOptions?.find(s => s?.value === period?.severity)?.label}
                  </span>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <span className={`px-2 py-1 text-xs rounded-full ${period?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                    {period?.isActive ? t('Active') : t('Inactive')}
                  </span>
                  <Button variant="ghost" size="sm" iconName="Edit" onClick={() => handleEditPeriod(period)} data-testid="leave-blackout-edit" />
                  <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => handleDeletePeriod(period?.id)} className="text-destructive hover:text-destructive/80" data-testid="leave-blackout-delete" />
                </div>
              </div>
              {period?.reason && <p className="text-sm text-muted-foreground mb-3">{period?.reason}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h5 className="text-sm font-medium text-muted-foreground mb-2">{t('Affected Departments')}:</h5>
                  <div className="flex flex-wrap gap-1">
                    {parseStoredIds(period?.departments)?.length > 0
                      ? parseStoredIds(period?.departments).map((id, i) => {
                          const dept = apiDepartments.find(d => String(d.id) === id);
                          return <span key={i} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">{dept?.name ?? id}</span>;
                        })
                      : <span className="text-xs text-muted-foreground/60">{t('None specified')}</span>
                    }
                  </div>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-muted-foreground mb-2">{t('Blocked Leave Types')}:</h5>
                  <div className="flex flex-wrap gap-1">
                    {parseStoredIds(period?.leaveTypes)?.length > 0
                      ? parseStoredIds(period?.leaveTypes).map((id, i) => {
                          const lt = apiLeaveTypes.find(l => String(l.id) === id);
                          return <span key={i} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">{lt?.name ?? id}</span>;
                        })
                      : <span className="text-xs text-muted-foreground/60">{t('None specified')}</span>
                    }
                  </div>
                </div>
              </div>
              <div className="pt-3 mt-3 border-t border-border text-sm">
                <span className="text-muted-foreground">{t('Notification')}: {period?.notificationDaysBefore} {t('days')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{editingPeriod ? t('Edit Blackout Period') : t('Add New Blackout Period')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Period Name')}</label>
              <Input type="text" value={formState?.name} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({ ...formState, name: e?.target?.value })} placeholder={t('e.g., Year-End Freeze')} data-testid="leave-blackout-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Start Date')}</label>
                <Input type="date" value={formState?.startDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({ ...formState, startDate: e?.target?.value })} data-testid="leave-blackout-start-date" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('End Date')}</label>
                <Input type="date" value={formState?.endDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({ ...formState, endDate: e?.target?.value })} data-testid="leave-blackout-end-date" />
              </div>
            </div>
            {formState.startDate && formState.endDate && formState.startDate > formState.endDate && (
              <p className="text-sm text-destructive" role="alert" data-testid="leave-blackout-date-range-error">{t('blackoutStartDateAfterEnd')}</p>
            )}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Reason')}</label>
              <textarea value={formState?.reason} onChange={(e) => setFormState({ ...formState, reason: e?.target?.value })} placeholder={t('Explain why this blackout period is necessary...')} rows={3} className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none" data-testid="leave-blackout-reason" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Affected Departments')}</label>
                <Select value={formState?.departments} onChange={(value: string[]) => setFormState({ ...formState, departments: value })} options={departmentOptions} multiple placeholder={t('Select departments')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Blocked Leave Types')}</label>
                <Select value={formState?.leaveTypes} onChange={(value: string[]) => setFormState({ ...formState, leaveTypes: value })} options={leaveTypeOptions} multiple placeholder={t('Select leave types')} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Severity Level')}</label>
                <Select value={formState?.severity} onChange={(value: string) => setFormState({ ...formState, severity: value as BlackoutSeverity })} options={severityOptions} data-testid="leave-blackout-severity" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Notification Period (days)')}</label>
                <Input type="number" value={formState?.notificationDaysBefore} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({ ...formState, notificationDaysBefore: parseInt(e?.target?.value) || 0 })} min="0" data-testid="leave-blackout-notification-days" />
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm} data-testid="leave-blackout-modal-cancel">{t('Cancel')}</Button>
            <Button variant="default" onClick={editingPeriod ? handleUpdatePeriod : handleSavePeriod} disabled={!formState?.name || !formState?.startDate || !formState?.endDate || (formState.startDate > formState.endDate) || isSaving} data-testid="leave-blackout-modal-save">
              {isSaving ? t('Saving...') : (editingPeriod ? t('Update') : t('Add'))} {!isSaving && t('Period')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlackoutDatesTab;
